import { createContext, useContext } from "react";
import type { ProductType } from "../navigation/types";
import type { IncomeTypeEnum, MlaStatusEnum, PrefillDataSourceEnum, StateEnum } from "../types/api";

export interface EligibilitySelection {
  criteriaId: string;
  optionId: string | null;
  freeformAnswer: string;
}

export interface StoreData {
  applicationMetadata?: Record<string, string>;
  lendingApplicationID?: string;
  resolvedInitialRouteByApplication?: Record<string, true>;
  selectedProduct?: string;
  productType?: ProductType;
  applicationState?: StateEnum;
  hasMultipleCreditCardProducts?: boolean;
  hasSelectedCreditCardProduct?: boolean;
  hasEligibilityCriteria?: boolean;
  hasLendingQuestions?: boolean;
  /** MLA covered-borrower answer, persisted as `mla_status` metadata on the application. */
  mlaStatus?: MlaStatusEnum;
  eligibilitySelection?: EligibilitySelection;
  tax_id?: string;
  date_of_birth?: string;
  accountApplicationUuid?: string;
  hasCoApplicant?: boolean;
  hasSeparateCoApplicantAddress?: boolean;
  isRefinanceSelected?: boolean;
  hasVehicleSelected?: boolean;
  incomeType?: IncomeTypeEnum;
  incomeDetailsId?: string;
  coApplicantIncomeType?: IncomeTypeEnum;
  coApplicantIncomeDetailsId?: string;
  plaidPublicToken?: string;
  skippedPlaidPrefill?: boolean;
  prefillDataSource?: PrefillDataSourceEnum;
  plaidPiiFormData?: {
    first_name: string;
    last_name: string;
    tax_id: string;
    date_of_birth: string;
    street: string;
    street2: string;
    city: string;
    region: string;
    postal_code: string;
  };
  phone?: string;
  email?: string;
}

interface StoreContextType {
  data: StoreData;
  updateData: (updates: Partial<StoreData>) => void;
  replaceData: (nextData: StoreData) => void;
  markInitialRouteResolved: (applicationUuid: string) => void;
  clearData: () => void;
  clearApplicationData: () => void;
  isInitialNavigation: boolean;
}

export const StoreContext = createContext<StoreContextType | undefined>(undefined);

export function useStore(): StoreContextType {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
}
